import Analytics from '@withlocals/withlocals-analytics';
import { Observable, Subject } from 'rxjs';
import { PushMessage } from 'src/services/PushMessageHandler';
import { OSNotification, WlWindow } from 'typings/globals/cordova';
import { useDeviceInfoStore } from '~/stores/device';

declare const window: WlWindow;

interface OneSignalNotificationEvent {
  complete?: any;
  action?: any;
  notification: OSNotification;
}

export interface OneSignalService {
  notificationOpens$: Observable<OneSignalNotificationEvent>;
  notificationReceives$: Observable<OneSignalNotificationEvent>;
  notificationOpensData$: Observable<PushMessage>;
  notificationReceivesData$: Observable<PushMessage>;
  requestPushPermissions: (autoContinueToSettings?: boolean) => void;
}

function isOneSignalAvailable(): boolean {
  return !!window?.plugins?.OneSignal;
}

export function getAnonymousId(): Promise<string> {
  return useDeviceInfoStore.getState().getOrCreateAnonymousDeviceId();
}

function loginAsAnonymous(anonymousId: string): void {
  window.plugins.OneSignal.login(anonymousId);
  window.plugins.OneSignal.User.addTags({
    anonymousDeviceId: anonymousId,
    isAuthenticated: 'false',
  });
}

export async function initializeForAnonymous(): Promise<void> {
  if (!isOneSignalAvailable()) {
    console.warn(
      '[onesignal] Cannot initialize for anonymous - plugin not available',
    );
    return;
  }
  const anonymousId = await getAnonymousId();
  console.log('[onesignal] Initializing for anonymous user:', anonymousId);
  loginAsAnonymous(anonymousId);
}

export async function migrateToAuthenticatedUser(
  accountId: string,
): Promise<void> {
  if (!accountId?.trim()) {
    console.warn('[onesignal] Cannot migrate - invalid accountId');
    return;
  }
  if (!isOneSignalAvailable()) {
    console.warn(
      '[onesignal] Cannot migrate to authenticated - plugin not available',
    );
    return;
  }
  const anonymousId = await getAnonymousId();
  console.log(
    '[onesignal] Migrating from anonymous to authenticated:',
    accountId,
  );
  window.plugins.OneSignal.logout();
  window.plugins.OneSignal.login(accountId);
  window.plugins.OneSignal.User.addTags({
    accountId: accountId,
    isAuthenticated: 'true',
    previousAnonymousDeviceId: anonymousId,
  });
  window.plugins.OneSignal.User.removeTag('anonymousDeviceId');
}

export async function revertToAnonymous(): Promise<void> {
  if (!isOneSignalAvailable()) {
    console.warn(
      '[onesignal] Cannot revert to anonymous - plugin not available',
    );
    return;
  }
  const anonymousId = await getAnonymousId();
  console.log('[onesignal] Reverting to anonymous user:', anonymousId);
  window.plugins.OneSignal.logout();
  loginAsAnonymous(anonymousId);
  window.plugins.OneSignal.User.removeTags([
    'accountId',
    'previousAnonymousDeviceId',
  ]);
}

export function OneSignal(): OneSignalService {
  console.debug('Starting service for OneSignal with id: ', ONESIGNAL_ID);

  if (!window?.plugins?.OneSignal) {
    console.warn(
      'OneSignal Service failed to start. OneSignal plugin is not available.',
    );
    return {
      notificationOpens$: Observable.of(),
      notificationReceives$: Observable.of(),
      notificationOpensData$: Observable.of(),
      notificationReceivesData$: Observable.of(),
      requestPushPermissions: () => {},
    };
  }

  window.plugins.OneSignal.initialize(ONESIGNAL_ID);

  void initializeForAnonymous();

  const notificationOpensSubject = new Subject<OneSignalNotificationEvent>();
  const notificationReceiveSubject = new Subject<OneSignalNotificationEvent>();

  window.plugins.OneSignal.Notifications.addEventListener(
    'click',
    (notificationReceivedEvent) => {
      notificationOpensSubject.next(notificationReceivedEvent);
    },
  );

  window.plugins.OneSignal.Notifications.addEventListener(
    'foregroundWillDisplay',
    (m: OneSignalNotificationEvent) => {
      notificationReceiveSubject.next(m);
      m.complete(m.notification);
    },
  );

  const notificationOpens$ = notificationOpensSubject.share();
  const notificationReceives$ = notificationReceiveSubject.share();

  const notificationOpensData$ = notificationOpens$
    .filter((m) => !!m.notification)
    .filter(({ notification }) => !!notification.additionalData)
    .map(({ notification }) => notification.additionalData as PushMessage);

  const notificationReceivesData$ = notificationReceives$
    .filter((m) => !!m.notification)
    .filter(({ notification }) => !!notification.additionalData)
    .map(({ notification }) => notification.additionalData as PushMessage);

  const requestPushPermissions = (autoContinueToSettings = false) => {
    window.plugins.OneSignal.Notifications.requestPermission(true).then(
      (accepted: boolean) => {
        Analytics.track('App', 'OneSignal Push permissions', accepted);
      },
    );

    if (autoContinueToSettings) {
      if (!window?.plugins?.OneSignal?.Notifications?.hasPermission?.()) {
        Analytics.track('App', 'Open Notification settings');
        window?.cordova?.plugins?.diagnostic?.switchToSettings?.();
      }
    }
  };
  return {
    notificationOpens$,
    notificationReceives$,
    requestPushPermissions,
    notificationOpensData$,
    notificationReceivesData$,
  };
}
